/**
 * Player Profile — career overview + per-format stats (imported from Cricinfo).
 *
 * 2026-06-12 redesign: stripped Recent Form, Wagon Wheel, Phase Splits,
 * Pitch Map, Vs Opposition, and all empty visualisations. Replaced the
 * stacked per-format cards with format tabs (Test / ODI / T20I / T20)
 * showing Batting / Bowling / Fielding aligned in three columns per tab.
 *
 * Reads from /api/v1/players/<id>/profile/.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MoreVertical, Pencil, PlayCircle, ShoppingBag, Trash2, User } from "lucide-react";
import api from "../../../api/axios";
import { deletePlayer } from "../../../api/coredata.api";
import useDialogDismiss from "../../../hooks/useDialogDismiss";
import useAuthStore from "../../../store/auth.store";
import HighlightsPlayer from "../../../components/players/HighlightsPlayer";

// True when a per-format stat row has any real data (matches > 0 in
// batting, bowling or fielding). Used to hide empty rows from the
// Career Overview aggregate and pick a sensible default tab.
const hasData = (r) => (r?.batting?.matches ?? 0) > 0
  || (r?.bowling?.matches ?? 0) > 0
  || (r?.fielding?.matches ?? 0) > 0;

// Backend emits a synthetic "OVERALL" tab (all T20 cricket combined —
// Cricinfo class=11: T20Is + all T20 franchises). Excluded from the
// Career Overview sum since it would double-count the T20 tab's data.
const OVERALL_CODE = "OVERALL";

// "1992-01-05" -> "5 Jan 1992 (34)". Returns "" for a null/garbage date so
// the header chip is dropped entirely rather than rendering "Invalid Date".
function formatDob(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const beforeBirthday = now.getMonth() < d.getMonth()
    || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate());
  if (beforeBirthday) age -= 1;
  const label = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return age >= 0 && age < 120 ? `${label} (${age})` : label;
}

// Header meta chip — renders nothing when the value is blank, so a
// partially-populated player doesn't show a row of empty labels.
function MetaChip({ label, value }) {
  if (!value) return null;
  return (
    <span>{label}: <span className="text-text-primary">{value}</span></span>
  );
}

export default function PlayerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // Boundary-highlights modal — see HighlightsPlayer component.
  const [highlights, setHighlights] = useState(null);  // { filter, title } | null

  useEffect(() => {
    let alive = true;
    api.get(`/api/v1/players/${id}/profile/`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.data?.detail || e.message));
    return () => { alive = false; };
  }, [id]);

  // Initial selected tab — prefer Overall when it carries data (headline
  // view for T20-heavy careers), else the first MatchType tab that has
  // stats, else the first tab. Backend pins Overall first, followed by
  // MatchTypes alphabetically.
  // Must sit above the early-return guard so the hook order stays stable.
  const importedStats = data?.imported_career_stats || [];
  const [activeFormat, setActiveFormat] = useState(null);
  useEffect(() => {
    if (importedStats.length > 0 && activeFormat == null) {
      const overall = importedStats.find((r) => r.format_code === OVERALL_CODE);
      if (overall && hasData(overall)) {
        setActiveFormat(overall.format);
      } else {
        const firstWithData = importedStats.find(hasData);
        setActiveFormat((firstWithData || importedStats[0]).format);
      }
    }
  }, [importedStats, activeFormat]);

  // Compact Career Overview — sum the per-format rows into one headline
  // row. Excludes the Overall pseudo-tab (its Cricinfo T20 data already
  // includes the T20I subset shown under the T20 MatchType tab).
  const overview = useMemo(() => {
    const perFormatRows = importedStats.filter((r) => r.format_code !== OVERALL_CODE);
    if (!perFormatRows.some(hasData)) return null;
    let matches = 0, runs = 0, outs = 0, balls = 0;
    let wkts = 0, runsConceded = 0;
    let catches = 0, stumpings = 0;
    for (const r of perFormatRows) {
      const bat = r?.batting || {};
      const bowl = r?.bowling || {};
      const field = r?.fielding || {};
      matches += (bat.matches || 0);
      runs += (bat.runs || 0);
      outs += Math.max(0, (bat.innings || 0) - (bat.not_outs || 0));
      balls += (bat.balls_faced || 0);
      wkts += (bowl.wickets || 0);
      runsConceded += (bowl.runs_conceded || 0);
      catches += (field.catches || 0);
      stumpings += (field.stumpings || 0);
    }
    return {
      matches,
      runs,
      bat_average: outs ? +(runs / outs).toFixed(2) : null,
      bat_strike_rate: balls ? +((runs * 100) / balls).toFixed(2) : null,
      wickets: wkts,
      bowl_average: wkts ? +(runsConceded / wkts).toFixed(2) : null,
      catches,
      stumpings,
    };
  }, [importedStats]);

  if (error) return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-4 rounded">{error}</div>
    </div>
  );
  if (!data) return <div className="p-6 text-text-secondary">Loading…</div>;

  const player = data.player || {};
  const fullName = player.nick_name || `${player.first_name || ""} ${player.last_name || ""}`.trim() || "(Unnamed)";

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary mb-4">
        <ArrowLeft className="w-4" /> Back
      </button>

      {/* Header */}
      <div className="bg-bg-primary border border-border-default rounded-2xl p-5 mb-4 flex items-start gap-4">
        <div className="w-16 h-16 bg-bg-secondary rounded-full flex items-center justify-center">
          <User className="w-8 h-8 text-text-secondary" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{fullName}</h1>
          {/* 2026-07-25 — was role + the two raw hand enums ("Bat: RIGHT").
              The player-profile ingest writes batting/bowling style, role
              and DOB, none of which reached this header. Now shows the
              Cricinfo phrasing and drops chips with no value. */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-secondary mt-1">
            <MetaChip label="Role" value={player.role_label || player.role} />
            <MetaChip
              label="Batting"
              value={player.batting_style || player.batting_hand}
            />
            <MetaChip
              label="Bowling"
              value={player.bowling_style_label || player.bowling_hand}
            />
            <MetaChip label="Country" value={player.nationality} />
            <MetaChip label="Born" value={formatDob(player.date_of_birth)} />
          </div>
        </div>
        {/* 2026-06-04 — explicit Highlights button. Previously the only
            way to open the highlights modal was clicking a chart dot or
            a Vs-opposition row, neither of which signposted as
            interactive. Surfacing a top-level button removes the
            "where are the highlights?" complaint. Empty filter →
            modal aggregates every boundary across the player's career. */}
        <button
          onClick={() => setHighlights({
            filter: {},
            title: `${fullName} · Boundary highlights`,
          })}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-button-primary text-white text-xs font-semibold hover:brightness-110 shrink-0"
          title="Watch every boundary this player has hit"
        >
          <PlayCircle className="w-4 h-4" />
          Highlights
        </button>
        <ProfileActionMenu playerId={player.id} onDeleted={() => navigate("/cricket/players")} />
      </div>

      {/* Career Overview — single compact row of headline metrics. */}
      {overview && (
        <div className="bg-bg-primary border border-border-default rounded-2xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
              Career overview
            </h3>
            <span className="text-[10px] text-text-secondary/60 italic">All formats combined</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            <OverviewTile label="Matches" value={overview.matches} />
            <OverviewTile label="Runs"    value={overview.runs} />
            <OverviewTile label="Bat avg" value={overview.bat_average ?? "—"} />
            <OverviewTile label="Bat SR"  value={overview.bat_strike_rate ?? "—"} />
            <OverviewTile label="Wickets" value={overview.wickets} />
            <OverviewTile label="Bowl avg" value={overview.bowl_average ?? "—"} />
            <OverviewTile label="Catches" value={overview.catches} />
          </div>
        </div>
      )}

      {/* Format tabs — one per active MatchType (empty tabs allowed). */}
      {importedStats.length > 0 && (
        <div className="bg-bg-primary border border-border-default rounded-2xl overflow-hidden mb-4">
          <div className="flex items-center gap-1 px-4 pt-3 border-b border-border-default overflow-x-auto">
            {importedStats.map((r) => (
              <button
                key={r.format}
                type="button"
                onClick={() => setActiveFormat(r.format)}
                className={`px-4 py-2 text-sm font-semibold rounded-t-md transition-colors whitespace-nowrap ${
                  activeFormat === r.format
                    ? "bg-bg-secondary text-text-primary border border-border-default border-b-bg-secondary -mb-px"
                    : "text-text-secondary hover:text-text-primary"
                }`}
              >
                {r.format_name || r.format}
              </button>
            ))}
            {(() => {
              const active = importedStats.find((r) => r.format === activeFormat);
              return active?.span ? (
                <span className="ml-auto text-xs text-text-secondary font-mono pb-1">{active.span}</span>
              ) : null;
            })()}
          </div>
          <div className="p-4 space-y-4">
            {(() => {
              const active = importedStats.find((r) => r.format === activeFormat) || importedStats[0];
              if (!active) return null;
              const bat = active.batting || {};
              const bowl = active.bowling || {};
              const field = active.fielding || {};
              // Order: key metrics FIRST inside each discipline (Runs, Avg,
              // SR, 100s for batting; Wickets, Avg, Econ, BBI for bowling;
              // Catches, Stumpings, Dismissals for fielding).
              return (
                <>
                  <DisciplineCard
                    title="Batting"
                    accent="emerald"
                    tiles={[
                      { label: "Runs",    value: bat.runs,            primary: true },
                      { label: "Avg",     value: bat.average,         primary: true },
                      { label: "SR",      value: bat.strike_rate,     primary: true },
                      { label: "100s",    value: bat.hundreds,        primary: true },
                      { label: "50s",     value: bat.fifties },
                      { label: "Highest", value: bat.highest_score },
                      { label: "Innings", value: bat.innings },
                      { label: "Matches", value: bat.matches },
                      { label: "Not outs", value: bat.not_outs },
                      { label: "Balls",   value: bat.balls_faced },
                      { label: "4s",      value: bat.fours },
                      { label: "6s",      value: bat.sixes },
                      { label: "Ducks",   value: bat.ducks },
                    ]}
                  />
                  <DisciplineCard
                    title="Bowling"
                    accent="indigo"
                    tiles={[
                      { label: "Wickets", value: bowl.wickets,         primary: true },
                      { label: "Avg",     value: bowl.average,         primary: true },
                      { label: "Econ",    value: bowl.economy,         primary: true },
                      { label: "BBI",     value: bowl.bbi || "—",      primary: true },
                      { label: "SR",      value: bowl.strike_rate },
                      { label: "BBM",     value: bowl.bbm || "—" },
                      { label: "Matches", value: bowl.matches },
                      { label: "Innings", value: bowl.innings },
                      { label: "Overs",   value: bowl.overs },
                      { label: "Maidens", value: bowl.maidens },
                      { label: "Runs",    value: bowl.runs_conceded },
                      { label: "5w",      value: bowl.five_wickets },
                      { label: "10w",     value: bowl.ten_wickets },
                    ]}
                  />
                  <DisciplineCard
                    title="Fielding"
                    accent="amber"
                    tiles={[
                      { label: "Catches",     value: field.catches,     primary: true },
                      { label: "Stumpings",   value: field.stumpings,   primary: true },
                      { label: "Dismissals",  value: field.dismissals,  primary: true },
                      { label: "Matches",     value: field.matches },
                      { label: "Innings",     value: field.innings },
                    ]}
                  />
                </>
              );
            })()}
          </div>
        </div>
      )}

      {highlights && (
        <HighlightsPlayer
          playerId={player.id}
          filter={highlights.filter}
          title={highlights.title}
          onClose={() => setHighlights(null)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────────── */

function OverviewTile({ label, value }) {
  return (
    <div className="bg-bg-secondary/40 border border-border-default rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-text-secondary">{label}</div>
      <div className="font-mono text-lg text-text-primary leading-tight">{value}</div>
    </div>
  );
}

// Horizontal discipline card — title bar + responsive grid of metric tiles.
// `primary: true` tiles render larger and pop in the discipline's accent
// colour so the eye lands on the headline numbers first.
const ACCENTS = {
  emerald: { dot: "bg-emerald-500", text: "text-emerald-300", chip: "bg-emerald-500/15 border-emerald-500/30" },
  indigo:  { dot: "bg-indigo-500",  text: "text-indigo-300",  chip: "bg-indigo-500/15 border-indigo-500/30" },
  amber:   { dot: "bg-amber-500",   text: "text-amber-300",   chip: "bg-amber-500/15 border-amber-500/30" },
};

function DisciplineCard({ title, tiles, accent = "emerald" }) {
  const palette = ACCENTS[accent] || ACCENTS.emerald;
  const primary = tiles.filter((t) => t.primary);
  const secondary = tiles.filter((t) => !t.primary);
  return (
    <div className="bg-bg-secondary/30 border border-border-default rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border-default bg-bg-secondary/40">
        <span className={`w-1.5 h-1.5 rounded-full ${palette.dot}`} />
        <span className="text-xs font-semibold uppercase tracking-widest text-text-primary">{title}</span>
      </div>
      {primary.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 pt-3">
          {primary.map((t) => (
            <MetricTile key={t.label} label={t.label} value={t.value} accent={palette} primary />
          ))}
        </div>
      )}
      {secondary.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 gap-2 px-3 py-3">
          {secondary.map((t) => (
            <MetricTile key={t.label} label={t.label} value={t.value} accent={palette} />
          ))}
        </div>
      )}
    </div>
  );
}

function MetricTile({ label, value, primary, accent }) {
  const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);
  return (
    <div className={`rounded-md border ${primary ? accent.chip : "bg-bg-primary border-border-default"} px-2 py-2 text-center`}>
      <div className={`font-mono ${primary ? `text-xl ${accent.text}` : "text-sm text-text-primary"} tabular-nums leading-tight`}>
        {fmt(value)}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-text-secondary mt-0.5">{label}</div>
    </div>
  );
}


// Three-dot action menu — quick links to Edit + Marketplace + Delete
// without forcing the operator to navigate back to the list to do
// anything beyond viewing stats. Delete is superuser-only and confirms
// before firing.
function ProfileActionMenu({ playerId, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const isSuperuser = useAuthStore((s) => !!s.user?.is_superuser);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const doDelete = async () => {
    setBusy(true);
    try {
      await deletePlayer(playerId);
      onDeleted?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || e?.message || "Delete failed");
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="More actions"
        className="p-1.5 rounded hover:bg-bg-secondary text-text-secondary hover:text-text-primary"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 w-48 bg-bg-primary border border-border-default rounded-lg shadow-xl py-1 z-30">
          <Link
            to={`/cricket/players/${playerId}/edit`}
            className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-bg-secondary/60"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit player
          </Link>
          <Link
            to="/marketplace"
            className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-bg-secondary/60"
          >
            <ShoppingBag className="w-3.5 h-3.5" /> Browse marketplace
          </Link>
          {isSuperuser && (
            <button
              onClick={() => { setOpen(false); setConfirmDelete(true); }}
              className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-rose-300 hover:bg-rose-500/10"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete player
            </button>
          )}
        </div>
      )}
      {confirmDelete && (
        <DeleteConfirm busy={busy} onCancel={() => setConfirmDelete(false)} onConfirm={doDelete} />
      )}
    </div>
  );
}

function DeleteConfirm({ busy, onCancel, onConfirm }) {
  const panelRef = useRef(null);
  const safeCancel = busy ? () => {} : onCancel;
  useDialogDismiss({ open: true, onClose: safeCancel, ref: panelRef });
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Delete this player"
        className="bg-bg-primary border border-border-default rounded-xl shadow-2xl max-w-sm w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold mb-2">Delete this player?</h3>
        <p className="text-sm text-text-secondary mb-4">
          This cannot be undone. Any team or match reference to this player will be unlinked.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="px-3 py-1.5 rounded bg-bg-secondary text-text-secondary text-sm">Cancel</button>
          <button onClick={onConfirm} disabled={busy} className="px-3 py-1.5 rounded bg-rose-600 text-white text-sm">
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}
